import { useState, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Check, Minus, ShieldCheck } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { StickySplit } from '@/components/landing/Editorial'
import { CtaBand, pinkCtaClass } from '@/components/landing/misc/CtaBand'
import { FaqAccordion } from '@/components/landing/misc/FaqAccordion'
import {
  HeroReveal,
  InteractiveDots,
  Pressable,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  TextReveal,
} from '@/components/motion'
import { cn } from '@/lib/utils'
import { pricingPlans, featureComparison, pricingFAQs, type PricingPlan } from '@/data/pricing'

type Interval = 'month' | 'year'

const EASE = [0.22, 1, 0.36, 1] as const

const planColumns = ['starter', 'professional', 'business', 'enterprise'] as const

const ledeClass =
  'border-l border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]'

function priceFor(plan: PricingPlan, interval: Interval) {
  const value = interval === 'year' ? plan.priceYearly : plan.price
  return value === 'custom' ? null : `$${value}`
}

/** Square segmented control: black fill marks the active interval. */
function BillingToggle({ value, onChange }: { value: Interval; onChange: (v: Interval) => void }) {
  const base =
    'flex h-12 items-center gap-2.5 px-5 text-sm font-bold uppercase tracking-wide transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-landing-accent)]'
  const active = 'bg-[var(--color-landing-primary)] text-white dark:bg-white dark:text-[var(--color-landing-primary)]'
  const idle =
    'text-[var(--color-landing-gray-700)] hover:text-[var(--color-landing-accent)] dark:text-[var(--color-landing-gray-300)] dark:hover:text-white'
  return (
    <div
      role="group"
      aria-label="Billing interval"
      className="inline-flex border border-[var(--color-landing-gray-900)] p-1 dark:border-white/60"
    >
      <button type="button" aria-pressed={value === 'month'} onClick={() => onChange('month')} className={cn(base, value === 'month' ? active : idle)}>
        Monthly
      </button>
      <button type="button" aria-pressed={value === 'year'} onClick={() => onChange('year')} className={cn(base, value === 'year' ? active : idle)}>
        Yearly
        <span className="bg-[#00d722] px-1.5 py-0.5 text-[11px] font-bold text-[var(--color-landing-primary)]">
          Save 17%
        </span>
      </button>
    </div>
  )
}

function PlanColumn({ plan, interval }: { plan: PricingPlan; interval: Interval }) {
  const featured = plan.highlighted
  const price = priceFor(plan, interval)
  const muted = featured ? 'text-white/80' : 'text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]'
  const strong = featured ? 'text-white' : 'text-[var(--color-landing-gray-900)] dark:text-white'
  const rule = featured ? 'border-white/25' : 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]'
  const href = plan.cta === 'Contact sales' ? '/contact' : '/register'

  return (
    <div
      className={cn(
        'relative flex h-full flex-col border-b border-r border-[var(--color-landing-gray-900)] dark:border-white/40',
        featured
          ? 'z-10 overflow-hidden border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] text-white dark:border-[var(--color-landing-accent)] lg:-mt-8 lg:h-[calc(100%+4rem)]'
          : 'bg-white dark:bg-[#0a0a0f]'
      )}
    >
      {featured && <InteractiveDots spacing={14} maxSize={3} intensity={0.7} />}
      <div className={cn('relative z-10 flex h-full flex-col p-6 xl:p-8', featured && 'lg:py-14 xl:py-16')}>
        <div className="flex min-h-9 items-start justify-between gap-3">
          <h3 className={cn('font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[0.02em]', strong)}>
            {plan.name}
          </h3>
          {featured && (
            <span className="-rotate-2 bg-[#ff00f2] px-2 py-1 text-xs font-bold uppercase tracking-wide text-white">
              Most popular
            </span>
          )}
        </div>
        <p className={cn('mt-3 min-h-[3.25rem] text-[15px] leading-[1.45]', muted)}>{plan.description}</p>

        <div className="mt-8 flex min-h-[5.5rem] items-end gap-2">
          {price ? (
            <>
              <motion.span
                key={price}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: EASE }}
                className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(2.75rem,4vw,4rem)] leading-[1.15] tracking-[-0.02em] tabular-nums', strong)}
              >
                {price}
              </motion.span>
              <span className={cn('pb-3 text-sm leading-tight', muted)}>
                /user/mo
                {interval === 'year' && <span className="block">billed yearly</span>}
              </span>
            </>
          ) : (
            <span className={cn('font-[family-name:var(--font-landing-display)] text-[1.75rem] uppercase leading-[1.3] tracking-[-0.02em]', strong)}>
              {plan.priceLabel}
            </span>
          )}
        </div>
        <p className={cn('mt-2 text-sm font-semibold', strong)}>{plan.teamSize}</p>

        <Link
          to={href}
          className={cn(
            'landing-cta landing-cta--md landing-btn-sweep mt-8 w-full',
            featured
              ? cn('landing-cta--solid landing-cta--on-color', pinkCtaClass)
              : plan.ctaVariant === 'default'
                ? 'landing-cta--solid'
                : 'landing-cta--outline'
          )}
        >
          {plan.cta}
          <ArrowRight className="h-4 w-4" />
        </Link>

        <div className={cn('mt-10 space-y-7 border-t pt-8', rule)}>
          {plan.features.map((group) => (
            <div key={group.category}>
              <h4 className={cn('mb-3 text-xs font-bold uppercase tracking-[0.08em]', featured ? 'text-white' : 'text-[var(--color-landing-gray-900)] dark:text-white')}>
                {group.category}
              </h4>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li
                    key={item.name}
                    className={cn(
                      'flex items-start gap-2.5 text-[15px] leading-snug',
                      item.included
                        ? featured
                          ? 'text-white'
                          : 'text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]'
                        : featured
                          ? 'text-white/55'
                          : 'text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-600)]'
                    )}
                  >
                    {item.included ? (
                      <Check className={cn('mt-0.5 h-4 w-4 shrink-0', featured ? 'text-white' : 'text-[var(--color-landing-accent)] dark:text-[#8b85ff]')} strokeWidth={2.5} aria-hidden />
                    ) : (
                      <Minus className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <span>
                      {item.name}
                      {!item.included && <span className="sr-only"> (not included)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ComparisonValue({ value }: { value: boolean | string }) {
  if (value === true) {
    return (
      <>
        <Check className="mx-auto h-5 w-5 text-[var(--color-landing-accent)] dark:text-[#8b85ff]" strokeWidth={2.5} aria-hidden />
        <span className="sr-only">Included</span>
      </>
    )
  }
  if (value === false) {
    return (
      <>
        <Minus className="mx-auto h-4 w-4 text-[var(--color-landing-gray-400)] dark:text-[var(--color-landing-gray-600)]" aria-hidden />
        <span className="sr-only">Not included</span>
      </>
    )
  }
  return <span className="text-sm font-semibold tabular-nums text-[var(--color-landing-gray-900)] dark:text-white">{value}</span>
}

export default function Pricing() {
  const [billingInterval, setBillingInterval] = useState<Interval>('month')

  return (
    <>
      <PageMeta
        title="Pricing - Simple, Transparent Plans | Templatean"
        description="Start free, scale as you grow. Every plan includes a 14-day trial and our 30-day satisfaction guarantee."
      />

      <LandingLayout>
        {/* Hero: highlighted H1, then the quote-bar lede and the billing
            toggle spread across one row (reference about-hero row). */}
        <section className="landing-container pt-16 pb-20 md:pt-24 lg:pb-28">
          <TextReveal
            as="h1"
            lines={['Simple, transparent ', <span key="hl" className="landing-highlight">pricing.</span>]}
            className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white"
          />
          <HeroReveal delay={0.25}>
            <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-end lg:justify-between lg:gap-24">
              <p className={cn(ledeClass, 'max-w-[52ch]')}>
                Start free, scale as you grow. Every plan includes a 14-day trial and our 30-day satisfaction guarantee.
              </p>
              <div className="flex flex-col gap-3 lg:items-end">
                <BillingToggle value={billingInterval} onChange={setBillingInterval} />
                <p className="text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                  Prices per user. No credit card required to start.
                </p>
              </div>
            </div>
          </HeroReveal>
        </section>

        {/* Plans: square bordered columns sharing hairlines; the recommended
            plan sits on a blue halftone band that overhangs the row. */}
        <section aria-label="Plans" className="landing-container pb-24 lg:pb-36">
          <StaggerContainer
            amount={0.1}
            className="grid border-l border-t border-[var(--color-landing-gray-900)] dark:border-white/40 md:grid-cols-2 lg:grid-cols-4"
          >
            {pricingPlans.map((plan) => (
              <StaggerItem key={plan.id} className={cn(plan.highlighted && 'relative z-10')}>
                <PlanColumn plan={plan} interval={billingInterval} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        {/* Guarantee: black band, statement left, terms right. */}
        <section className="relative overflow-hidden bg-[var(--color-landing-primary)] text-white dark:bg-[#12121a]">
          <div className="landing-container landing-section-sm grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-end lg:gap-24">
            <div className="flex items-start gap-6">
              <span aria-hidden className="hidden h-16 w-16 shrink-0 items-center justify-center bg-white text-[var(--color-landing-primary)] sm:flex">
                <ShieldCheck className="h-8 w-8" strokeWidth={1.75} />
              </span>
              <TextReveal as="h2" className="landing-text-display-xl text-white">
                30-day satisfaction{' '}
                <span className="landing-highlight">guarantee</span>
              </TextReveal>
            </div>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="max-w-[48ch] border-l border-white/30 pl-5 landing-text-body-lg text-white/80">
                Try any paid plan risk-free. If you're not completely satisfied within the first 30 days,
                we'll refund your payment in full—no questions asked. Your success is our priority.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Feature comparison: full-width table, hairline rows, the
            recommended column tinted to match its band. */}
        <section className="landing-section">
          <div className="landing-container">
            <div className="mb-14 grid gap-8 lg:mb-20 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-24">
              <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
                Compare all{' '}
                <span className="landing-highlight landing-highlight--secondary">features</span>
              </TextReveal>
              <ScrollReveal direction="none" delay={0.15}>
                <p className={cn(ledeClass, 'max-w-md')}>See exactly what's included in each plan</p>
              </ScrollReveal>
            </div>

            <ScrollReveal direction="none" amount={0.05}>
              <div className="relative -mx-[clamp(1.5rem,2vw,2rem)] overflow-x-auto px-[clamp(1.5rem,2vw,2rem)] lg:mx-0 lg:overflow-visible lg:px-0">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <caption className="sr-only">Feature comparison across Starter, Professional, Business and Enterprise plans</caption>
                  <thead>
                    <tr className="border-b border-[var(--color-landing-gray-900)] dark:border-white/60">
                      <th scope="col" className="sticky left-0 z-10 w-40 bg-white md:w-[34%] py-5 pr-6 align-bottom text-sm font-semibold text-[var(--color-landing-gray-600)] dark:bg-[#0a0a0f] dark:text-[var(--color-landing-gray-400)]">
                        Feature
                      </th>
                      {pricingPlans.map((plan) => {
                        const price = priceFor(plan, billingInterval)
                        return (
                          <th
                            key={plan.id}
                            scope="col"
                            className={cn(
                              'px-4 py-5 text-center align-bottom',
                              plan.highlighted ? 'bg-[var(--color-landing-accent)] text-white' : 'text-[var(--color-landing-gray-900)] dark:text-white'
                            )}
                          >
                            <span className="block font-[family-name:var(--font-landing-subdisplay)] text-base font-bold uppercase tracking-[0.02em]">
                              {plan.name}
                            </span>
                            <span className={cn('mt-1 block text-sm font-normal tabular-nums', plan.highlighted ? 'text-white/80' : 'text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]')}>
                              {price ? `${price}/user/mo` : 'Custom'}
                            </span>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {featureComparison.categories.map((category) => (
                      <Fragment key={category.name}>
                        <tr>
                          <th
                            scope="colgroup"
                            colSpan={5}
                            className="sticky left-0 bg-white pt-10 pb-3 text-xs font-bold uppercase tracking-[0.08em] text-[var(--color-landing-gray-900)] dark:bg-[#0a0a0f] dark:text-white"
                          >
                            {category.name}
                          </th>
                        </tr>
                        {category.features.map((feature) => (
                          <tr
                            key={feature.name}
                            className="border-t border-[var(--color-landing-gray-200)] transition-colors duration-150 hover:bg-[var(--color-landing-gray-50)] dark:border-[var(--color-landing-gray-800)] dark:hover:bg-white/[0.03]"
                          >
                            <th
                              scope="row"
                              className="sticky left-0 bg-white py-3.5 pr-6 text-[15px] font-normal text-[var(--color-landing-gray-700)] dark:bg-[#0a0a0f] dark:text-[var(--color-landing-gray-300)]"
                            >
                              {feature.name}
                            </th>
                            {planColumns.map((col) => (
                              <td
                                key={col}
                                className={cn(
                                  'px-4 py-3.5 text-center',
                                  col === 'professional' && 'bg-[var(--color-landing-accent)]/[0.06] dark:bg-[var(--color-landing-accent)]/[0.14]'
                                )}
                              >
                                <ComparisonValue value={feature[col]} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* FAQ: sticky header left, hairline accordion right. */}
        <section className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
          <div className="landing-container">
            <StickySplit
              side="left"
              headerWidth="narrow"
              header={
                <>
                  <TextReveal as="h2" className="landing-text-display-xl mb-10 text-[var(--color-landing-gray-900)] dark:text-white">
                    Frequently asked{' '}
                    <span className="landing-highlight">questions.</span>
                  </TextReveal>
                  <ScrollReveal direction="none" delay={0.15}>
                    <p className={cn(ledeClass, 'mb-10 max-w-md')}>Everything you need to know about our pricing and plans</p>
                  </ScrollReveal>
                  <ScrollReveal direction="none" delay={0.25}>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-3 text-base font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] transition-colors hover:text-[var(--color-landing-accent)] dark:text-white"
                    >
                      Ask us directly
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </ScrollReveal>
                </>
              }
            >
              <ScrollReveal direction="none" amount={0.1}>
                <FaqAccordion items={pricingFAQs} />
              </ScrollReveal>
            </StickySplit>
          </div>
        </section>

        <CtaBand
          title={
            <>
              Ready to transform{' '}
              <br className="hidden md:block" />
              your document workflows?
            </>
          }
          description="Start your 14-day free trial today. No credit card required."
          actions={
            <>
              <Pressable>
                <Link
                  to="/register"
                  className={cn('landing-cta landing-cta--solid landing-cta--on-color landing-cta--lg landing-btn-sweep', pinkCtaClass)}
                >
                  Start free trial
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Pressable>
              <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--on-color landing-cta--lg landing-btn-sweep">
                Talk to sales
              </Link>
            </>
          }
        />
      </LandingLayout>
    </>
  )
}
